import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/Button";
import { cn } from "../lib/utils";
import { trackTryKnoothClick } from "../lib/analytics";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* Close menu on route change / resize to desktop */
  useEffect(() => {
    const close = () => setMenuOpen(false);
    window.addEventListener("resize", () => {
      if (window.innerWidth >= 768) close();
    });
    return () => window.removeEventListener("resize", close);
  }, []);

  /* Lock body scroll when menu is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] flex justify-center pt-6 px-4 pointer-events-none">
      <nav
        className={cn(
          "pointer-events-auto relative z-10 flex items-center justify-between pl-6 pr-3 py-3 rounded-full transition-all duration-300",
          "bg-white/70 backdrop-blur-xl border border-white/20",
          "w-full max-w-5xl"
        )}
      >
        <Link to="/" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
          <picture>
            <source srcSet="/assets/logo.webp" type="image/webp" />
            <img src="/assets/logo.png" alt="knooth logo" width={48} height={48} className="w-8 h-8 rounded-lg object-contain" />
          </picture>
          <span className="text-lg font-bold tracking-tight text-[#1D1D1F]">knooth</span>
        </Link>

        <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          <Link to="/#features" className="text-[15px] font-medium text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors">Features</Link>
          <Link to="/#pricing" className="text-[15px] font-medium text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors">Pricing</Link>
          <Link to="/guide" className="text-[15px] font-medium text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors">Guide</Link>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
            target="_blank"
            rel="noreferrer"
            data-analytics="try-knooth"
            data-location="navbar"
            onClick={() => trackTryKnoothClick("navbar")}
            className="hidden md:inline-flex rounded-full border-black/10 hover:bg-black/5 text-[#1D1D1F] font-medium px-5"
          >
            Try knooth
          </Button>

          {/* Hamburger — mobile only */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            <span className="sr-only">{menuOpen ? "Close" : "Menu"}</span>
            <span
              className={cn(
                "absolute h-[2px] w-[18px] bg-[#1D1D1F] rounded-full transition-all duration-300",
                menuOpen ? "rotate-45 translate-y-0" : "-translate-y-[5px]"
              )}
            />
            <span
              className={cn(
                "absolute h-[2px] w-[18px] bg-[#1D1D1F] rounded-full transition-all duration-300",
                menuOpen ? "opacity-0" : "opacity-100"
              )}
            />
            <span
              className={cn(
                "absolute h-[2px] w-[18px] bg-[#1D1D1F] rounded-full transition-all duration-300",
                menuOpen ? "-rotate-45 translate-y-0" : "translate-y-[5px]"
              )}
            />
          </button>
        </div>
      </nav>

      {/* ── Mobile menu overlay ────────────────────────────────── */}
      <div
        className={cn(
          "pointer-events-auto fixed inset-0 z-[1] bg-white/20 backdrop-blur-3xl backdrop-saturate-150 transition-all duration-300 md:hidden",
          menuOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-4 pointer-events-none"
        )}
      >
        <div className="flex flex-col items-center gap-6 pt-[120px]">
          <Link
            to="/#features"
            onClick={() => setMenuOpen(false)}
            className="text-[20px] font-semibold text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors"
          >
            Features
          </Link>
          <Link
            to="/#pricing"
            onClick={() => setMenuOpen(false)}
            className="text-[20px] font-semibold text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors"
          >
            Pricing
          </Link>
          <Link
            to="/guide"
            onClick={() => setMenuOpen(false)}
            className="text-[20px] font-semibold text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors"
          >
            Guide
          </Link>

          <div className="mt-4">
            <Button
              variant="outline"
              size="md"
              href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
              target="_blank"
              rel="noreferrer"
              data-analytics="try-knooth"
              data-location="navbar-mobile"
              onClick={() => {
                trackTryKnoothClick("navbar-mobile");
                setMenuOpen(false);
              }}
              className="rounded-full border-black/10 hover:bg-black/5 text-[#1D1D1F] font-medium px-8"
            >
              Try knooth
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Navbar };
